import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { clientApi, ClientListResponse } from '../api/clientsApi';
import { Client, ClientFilters, PaginationState, ClientState } from '../types/clients';

const initialState: ClientState = {
    list: [],
    loading: false,
    error: null,
    filters: {
        status: 'Active'
    },
    pagination: {
        current: 1,
        pageSize: 20,
        total: 0
    },
    selectedClient: null
};

// Async thunks
export const fetchClients = createAsyncThunk(
    'clients/fetchClients',
    async (params: { filters?: ClientFilters; pagination?: Partial<PaginationState>; showArchived?: boolean; showDeactivated?: boolean }, { rejectWithValue }) => {
        try {
            const response: ClientListResponse = await clientApi.getClients(params);

            const result = {
                clients: response.results,
                total: response.count,
            };

            return result;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to fetch clients');
        }
    }
);

export const createClient = createAsyncThunk(
    'clients/createClient',
    async (clientData: any, { rejectWithValue }) => {
        try {
            const newClient = await clientApi.createClient(clientData);
            return newClient;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to create client');
        }
    }
);

export const updateClient = createAsyncThunk(
    'clients/updateClient',
    async ({ id, data }: { id: string; data: any }, { rejectWithValue }) => {
        try {
            const updatedClient = await clientApi.updateClient(id, data);
            return updatedClient;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to update client');
        }
    }
);

export const deleteClient = createAsyncThunk(
    'clients/deleteClient',
    async (id: string, { rejectWithValue }) => {
        try {
            await clientApi.deleteClient(id);
            return id;
        } catch (error: any) {
            return rejectWithValue(error.response?.data?.message || 'Failed to delete client');
        }
    }
);

const clientSlice = createSlice({
    name: 'clients',
    initialState,
    reducers: {
        setFilters: (state, action: PayloadAction<ClientFilters>) => {
            state.filters = { ...state.filters, ...action.payload };
        },
        clearFilters: (state) => {
            state.filters = {};
        },
        setPagination: (state, action: PayloadAction<Partial<PaginationState>>) => {
            state.pagination = { ...state.pagination, ...action.payload };
        },
        setSelectedClient: (state, action: PayloadAction<Client | null>) => {
            state.selectedClient = action.payload;
        },
        clearError: (state) => {
            state.error = null;
        }
    },
    extraReducers: (builder) => {
        builder
            // Fetch clients
            .addCase(fetchClients.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(fetchClients.fulfilled, (state, action) => {
                state.loading = false;
                state.list = action.payload.clients;
                state.pagination.total = action.payload.total;
            })
            .addCase(fetchClients.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Create client
            .addCase(createClient.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(createClient.fulfilled, (state, action) => {
                state.loading = false;
                state.list.unshift(action.payload);
                state.pagination.total += 1;
            })
            .addCase(createClient.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Update client
            .addCase(updateClient.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(updateClient.fulfilled, (state, action) => {
                state.loading = false;
                const index = state.list.findIndex(client => client.id === action.payload.id);
                if (index !== -1) {
                    state.list[index] = action.payload;
                }
            })
            .addCase(updateClient.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })

            // Delete client
            .addCase(deleteClient.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(deleteClient.fulfilled, (state, action) => {
                state.loading = false;
                state.list = state.list.filter(client => client.id !== action.payload);
                state.pagination.total -= 1;
            })
            .addCase(deleteClient.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    }
});

export const {
    setFilters,
    clearFilters,
    setPagination,
    setSelectedClient,
    clearError
} = clientSlice.actions;

export default clientSlice.reducer;
